import { PlusIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Layout from '@/components/template/Layout';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import HeadTitle from '@/components/ui/custom/HeadTitle';
import PageTitle from '@/components/ui/custom/PageTitle';
import useAuth from '@/data/hook/useAuth';
import useStudentContext from '@/data/hook/useStudentContext';
import { isStaff } from '@/functions/auth';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { LEI_FELCA } from '@/lib/growthbook';
import { ROUTES } from '@/utils/routes';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  ProjectFiltersFields,
  ProjectPageFilters,
} from '../../components/projects/ProjectPageFilters';
import useProjectFieldsAdapter from '../../components/projects/ProjectPageFilters.hooks';

export default function ProjectPage() {
  const { t } = useTranslation('translation', { keyPrefix: 'projects.page' });
  const { user } = useAuth();
  const navigate = useNavigate();
  const { validEnrollment } = useStudentContext();
  const isLeiFelca = useFeatureIsOn(LEI_FELCA);
  const [filterFields, setFilterFields] = useState<ProjectFiltersFields>({});

  const { pathname } = useLocation();

  const isHackathonRanking = pathname.includes('hackathon/ranking');

  const layoutClassname = pathname.includes('/projects/section')
    ? 'overflow-visible'
    : '';

  const filters = useProjectFieldsAdapter(filterFields);

  return (
    <Layout className={layoutClassname}>
      <HeadTitle routeInfo={t('title')} />

      <div className="flex justify-between lg:w-full items-center mb-2">
        <PageTitle headingText={t('title')} position="mb-0 md:mb-0" />
        <ConditionalRenderer
          condition={
            isLeiFelca
              ? isStaff(user?.userType)
              : validEnrollment || isStaff(user?.userType)
          }
        >
          <MainButton
            onClick={() => navigate(ROUTES.PROJECTS.CREATE)}
            icon={<PlusIcon />}
            text={t('newProject')}
            color="custom"
            className="flex-row-reverse font-600 text-primary gap-1.5 mr-7 lg:mr-0"
          />
        </ConditionalRenderer>
      </div>
      <main className="w-full">
        <ConditionalRenderer condition={!isHackathonRanking}>
          <ProjectPageFilters
            filters={filterFields}
            onFilter={setFilterFields}
          />
        </ConditionalRenderer>
        <Outlet context={{ filters }} />
      </main>
    </Layout>
  );
}
